import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { WebService } from './web.service';

export interface FuturesPosition {
  Source: 'Legacy' | 'RMS';
  ReserveOrderDetailID: number | null; // legacy only
  ReserveOrderID: number | null;       // legacy only
  CompanyID: number;
  Customer: string;
  PurchaseDate: string | null;
  TraderID: number | null;
  TraderName: string;
  InventoryID: number | null;
  InventoryTypeName: string | null;
  FutureCompositeID: number | null;
  FutureCompositeName: string | null;
  FuturesMonth: number | null;
  FuturesYear: number | null;
  FND: string | null;
  ExchangeName: string | null;
  ContractWeight: number | null;
  Contracts: number;
  WeightLbs: number; // remaining weight for RMS contracts
  AllocatedWeightLbs: number;
  Price: number | null;
  ContractStartDate: string | null;
  ContractEndDate: string | null;
  ConfirmSent: boolean | null;
  ConfirmSentTime: string | null;
  InsertedDate: string | null;
  IsActive: boolean;
  // null until first save (legacy)
  FuturesPositionID: number | null;
  OriginalWeightLbs: number | null;
  PriceUnit: PriceUnit;
  TerminalID: number | null;
  TerminalAllocation: string | null;
  ContractNote: string | null;
  ContractStatusID: number;
  ContractStatusName: string;
  ApplicationStatus: string | null;
  FuturesUpdatedDate: string | null;
}

// set by the exchange (futures_exchange.PriceUnit)
export type PriceUnit = 'LB' | 'MT';

export interface PositionKey {
  ReserveOrderDetailID: number | null;
  FuturesPositionID: number | null;
}

export function positionKey(p: { ReserveOrderDetailID: number | null; FuturesPositionID: number | null }): PositionKey {
  return { ReserveOrderDetailID: p.ReserveOrderDetailID ?? null, FuturesPositionID: p.FuturesPositionID ?? null };
}

export interface FuturesPositionPayload extends PositionKey {
  TerminalID: number | null;
  ContractNote: string | null;
  Price?: number | null; // RMS contracts only
}

export interface NewFuturesContract {
  CompanyID: number;
  TraderID: number | null;
  FutureCompositeID: number;
  WeightLbs: number;
  Price: number | null;
  PurchaseDate: string | null;
  TerminalID: number | null;
  ContractNote: string | null;
}

export interface FuturesMonthOption {
  FutureCompositeID: number;
  FutureCompositeName: string;
  FND: string | null;
  Exchange: number;
}

export interface FuturesExchange {
  ExchangeID: number;
  ExchangeName: string;
  ContractWeight: number;
  PriceUnit: PriceUnit;
}

export interface FuturesNote {
  FuturesNoteID: number;
  NoteType: string;
  IsInternal: boolean;
  RelatedStep: string | null;
  FollowUpRequired: boolean;
  FollowUpDate: string | null;
  NoteText: string;
  CreatedByName: string;
  CreatedDate: string;
  UpdatedByName: string | null;
  UpdatedDate: string | null;
}

export interface FuturesHistory {
  FuturesActionHistoryID: number;
  ActionType: string;
  ActionDetail: string | null;
  EntityType: string | null;
  ReferenceID: string | null;
  FieldName: string | null;
  BeforeValue: string | null;
  AfterValue: string | null;
  ActionByName: string;
  ActionDate: string;
  SystemSource: string;
  IPAddress: string | null;
}

export interface FuturesDocument {
  FuturesContractDocumentID: number;
  VersionNumber: number;
  FilePath: string;
  GeneratedByName: string;
  GeneratedDate: string;
  SignedDate: string | null;
  SignedFilePath: string | null;
  TimesSent: number;
}

export interface FuturesAllocation {
  FuturesAllocationID: number;
  ReserveOrderID: number;
  ReserveOrderDetailID: number;
  InventoryID: number;
  InventoryTypeName: string | null;
  Bags: number;
  BagWeightLbs: number;
  WeightLbs: number;
  Price: number;
  Basis: number | null;
  Differential: number | null;
  CreatedByName: string;
  CreatedDate: string;
}

export interface AllocationLot {
  InventoryID: number;
  InventoryTypeID: number;
  InventoryTypeName: string;
  WarehouseID: number | null;
  WarehouseName: string | null;
  LotStatus: 'In warehouse' | 'Coming in';
  DateIn: string | null;
  FinalPosition: string | null;
  BagWeightLbs: number;
  AvailableQty: number;
  ReservedBags: number;
  FreeBags: number;
  MaxBags: number;
  RemainingWeightLbs: number;
  SkuDifferential: number | null; // latest ask differential from SKU Pricing
  SkuPriceDate: string | null;
}

export interface FuturesNotification {
  FuturesNotificationLogID: number;
  FuturesPositionID?: number;
  ReserveOrderDetailID?: number | null;
  ReserveOrderID?: string;
  Customer?: string;
  FuturesMonth?: string;
  OriginalWeightLbs?: number;
  NotificationType: string;
  Recipients: string | null;
  Subject?: string | null;
  DeliveryStatus: 'Pending' | 'Delivered' | 'Failed';
  RetryCount: number;
  CreatedDate: string;
  LastAttemptDate: string | null;
  NextRetryDate: string | null;
  DeliveredDate: string | null;
  ErrorMessage: string | null;
  TriggeredByName: string;
}

export interface NotificationResult {
  delivered: boolean;
  pending: boolean;
  error: string | null;
  recipients: string;
}

export interface FuturesNotePayload extends PositionKey {
  FuturesNoteID: number | null;
  NoteType: string;
  IsInternal: boolean;
  RelatedStep: string | null;
  FollowUpRequired: boolean;
  FollowUpDate: string | null;
  NoteText: string;
}

const keyParams = (key: PositionKey) => [
  { name: 'ReserveOrderDetailID', value: key.ReserveOrderDetailID ?? '' },
  { name: 'FuturesPositionID', value: key.FuturesPositionID ?? '' }
];

@Injectable({
  providedIn: 'root'
})
export class FuturesDataService {
  constructor(private web: WebService) { }

  get currentUserGUID(): string | null {
    return localStorage.getItem('GUID');
  }

  get currentUserEmail(): string | null {
    return localStorage.getItem('email');
  }

  getTerminals(): Observable<{ TerminalID: number; TerminalName: string | null }[]> {
    return this.web.getWebCall('data', 'coffee/entry-terminals');
  }

  getLookups(): Observable<{ Months: FuturesMonthOption[]; Exchanges: FuturesExchange[] }> {
    return this.web.getWebCall('futures', 'lookups');
  }

  getCustomers(): Observable<any[]> {
    return this.web.getWebCall('customers', 'coffee/customers', { params: [{ name: 'IncludeInactive', value: 0 }] });
  }

  getTraders(): Observable<{ TraderID: number; FullName: string; IsActive: boolean }[]> {
    return this.web.getWebCall('data', 'coffee/traders');
  }

  getPositions(includeClosed = false): Observable<FuturesPosition[]> {
    return this.web.getWebCall('futures', 'positions',
      includeClosed ? { params: [{ name: 'IncludeClosed', value: 1 }] } : undefined);
  }

  createContract(payload: NewFuturesContract): Observable<{ success: boolean; FuturesPositionID: number; Notification: NotificationResult | null }> {
    return this.web.getWebCall('futures', 'contracts/create', {
      body: { ...payload, ActionByGUID: this.currentUserGUID, FromEmail: this.currentUserEmail }
    });
  }

  savePosition(payload: FuturesPositionPayload): Observable<{ success: boolean; FuturesPositionID: number; Notification: NotificationResult | null }> {
    return this.web.getWebCall('futures', 'positions/save', {
      body: { ...payload, ActionByGUID: this.currentUserGUID, FromEmail: this.currentUserEmail }
    });
  }

  notifyRisk(key: PositionKey): Observable<{ success: boolean; Notification: NotificationResult }> {
    return this.web.getWebCall('futures', 'notify-risk', {
      body: { ...key, ActionByGUID: this.currentUserGUID, FromEmail: this.currentUserEmail }
    });
  }

  getRiskNotifications(): Observable<FuturesNotification[]> {
    return this.web.getWebCall('futures', 'risk-notifications');
  }

  getActivity(key: PositionKey): Observable<{
    Notes: FuturesNote[]; History: FuturesHistory[]; Documents: FuturesDocument[];
    Notifications: FuturesNotification[]; Allocations: FuturesAllocation[];
  }> {
    return this.web.getWebCall('futures', 'activity', { params: keyParams(key) });
  }

  saveNote(payload: FuturesNotePayload): Observable<{ success: boolean; FuturesNoteID: number }> {
    return this.web.getWebCall('futures', 'notes/save', {
      body: { ...payload, ActionByGUID: this.currentUserGUID }
    });
  }

  saveContract(key: PositionKey, base64: string): Observable<{ success: boolean; VersionNumber: number }> {
    return this.web.getWebCall('futures', 'contracts/save', {
      body: { ...key, Base64: base64, ActionByGUID: this.currentUserGUID }
    });
  }

  getContractFile(path: string): Observable<{ base64: string }> {
    return this.web.getWebCall('futures', 'contracts/file', { params: [{ name: 'path', value: path }] });
  }

  sendEmail(payload: any): Observable<any> {
    return this.web.getWebCall('email', 'email/send', { body: payload });
  }

  logContractSent(key: PositionKey, documentId: number, recipients: string[], success: boolean, errorMessage?: string): Observable<any> {
    return this.web.getWebCall('futures', 'contracts/sent', {
      body: {
        ...key,
        FuturesContractDocumentID: documentId,
        Recipients: recipients,
        Success: success,
        ErrorMessage: errorMessage ?? null,
        ActionByGUID: this.currentUserGUID
      }
    });
  }

  markSigned(key: PositionKey, documentId: number, signedDate: string, base64: string | null): Observable<any> {
    return this.web.getWebCall('futures', 'contracts/signed', {
      body: {
        ...key,
        FuturesContractDocumentID: documentId,
        SignedDate: signedDate,
        Base64: base64,
        ActionByGUID: this.currentUserGUID
      }
    });
  }

  clearSignature(key: PositionKey, reason: string): Observable<any> {
    return this.web.getWebCall('futures', 'contracts/clear-signature', {
      body: { ...key, Reason: reason, ActionByGUID: this.currentUserGUID }
    });
  }

  getAllocationLots(futuresPositionId: number): Observable<AllocationLot[]> {
    return this.web.getWebCall('futures', 'allocation-lots', { params: [{ name: 'FuturesPositionID', value: futuresPositionId }] });
  }

  allocateToReserve(futuresPositionId: number, inventoryId: number, bags: number, price: number, differential: number | null): Observable<{
    success: boolean; ReserveOrderID: number; FuturesAllocationID: number; WeightLbs: number; RemainingWeightLbs: number; AbsorbedLbs: number;
  }> {
    return this.web.getWebCall('futures', 'allocations/create', {
      body: { FuturesPositionID: futuresPositionId, InventoryID: inventoryId, Bags: bags, Price: price, Differential: differential, ActionByGUID: this.currentUserGUID }
    });
  }

  getAuditReport(): Observable<any[]> {
    return this.web.getWebCall('futures', 'audit');
  }
}
